import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";

/**
 * 2026-04-23: 페이지 단위 로딩 화면.
 *   기존 "이야기를 불러오는 중…" 단순 텍스트 → ember 광휘 + 떠있는 sigil 링 + 타이포.
 *   씬 전환 후 데이터 fetch 가 길어질 때 "양피지에 잉크가 번지는" 느낌의 기다림.
 */
export interface CodexLoaderProps {
    label?: string;
    hint?: string;
    kicker?: string;
}

export function CodexLoader({
    label = "이야기를 불러오는 중…",
    hint,
    kicker = "기록장을 펼치는 중",
}: CodexLoaderProps) {
    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none select-none">
            {/* 중앙 ember 광휘 — 부드럽게 숨쉬듯 */}
            <motion.div
                aria-hidden
                initial={{ scale: 0.94, opacity: 0.5 }}
                animate={{ scale: [0.94, 1.04, 0.94], opacity: [0.5, 0.85, 0.5] }}
                transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute rounded-full"
                style={{
                    width: 280,
                    height: 280,
                    background: `radial-gradient(circle, ${tokens.color.ember}33 0%, ${tokens.color.ember}12 40%, transparent 70%)`,
                    filter: "blur(8px)",
                }}
            />

            {/* sigil 링 — 회전하는 이중 대시 원 */}
            <div className="relative mb-l" style={{ width: 84, height: 84 }}>
                <motion.svg
                    viewBox="-50 -50 100 100"
                    width={84}
                    height={84}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                    style={{ position: "absolute", inset: 0, filter: `drop-shadow(0 0 10px ${tokens.color.ember}88)` }}
                >
                    <circle cx={0} cy={0} r={42} fill="none" stroke={tokens.color.brass} strokeWidth={1.2} strokeDasharray="3 3" opacity={0.7} />
                </motion.svg>
                <motion.svg
                    viewBox="-50 -50 100 100"
                    width={84}
                    height={84}
                    animate={{ rotate: -360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    style={{ position: "absolute", inset: 0, filter: `drop-shadow(0 0 8px ${tokens.color.brass}AA)` }}
                >
                    <circle cx={0} cy={0} r={32} fill="none" stroke={tokens.color.ember} strokeWidth={1} strokeDasharray="1.5 2" opacity={0.9} />
                </motion.svg>
                <motion.div
                    className="absolute inset-0 flex items-center justify-center font-display"
                    animate={{ opacity: [0.65, 1, 0.65] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                        color: tokens.color.ember,
                        fontSize: 28,
                        textShadow: `0 0 14px ${tokens.color.ember}, 0 0 4px #FFF6D8`,
                    }}
                >
                    ✦
                </motion.div>
            </div>

            {/* 짧은 안내 문구 */}
            {kicker && (
                <div
                    className="font-body mb-xs"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: 16.5,
                        letterSpacing: "0.14em",
                        opacity: 0.92,
                        textShadow: `${tokens.color.ink} 0px 2px 8px, ${tokens.color.ink} 0px 0px 14px`,
                    }}
                >
                    {kicker}
                </div>
            )}

            {/* 본 라벨 */}
            <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: tokens.easing.decelerate }}
                className="font-display"
                style={{
                    color: tokens.color.parchment,
                    fontSize: "clamp(18px, 1.4vw, 22px)",
                    letterSpacing: "0.06em",
                    textShadow: `0 0 14px ${tokens.color.ember}44, 0 1px 0 ${tokens.color.ink}`,
                }}
                role="status"
                aria-live="polite"
            >
                {label}
            </motion.div>

            {hint && (
                <div
                    className="mt-xs font-body italic"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: 15.5,
                        letterSpacing: "0.04em",
                        opacity: 0.92,
                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                    }}
                >
                    {hint}
                </div>
            )}
        </div>
    );
}
